import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import styled from "styled-components";
import { motion, useAnimation } from "framer-motion";

const FeatureCard = styled(Box)`
  border-radius: 16px;
  padding: 32px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
  }
`;

const CardContent = styled(Box)`
  position: relative;
  z-index: 2;
  background: ${props => props.theme.palette.background.paper};
  border-radius: 12px;
  padding: 24px;
  margin-top: auto;
  text-align: center;
`;

const CardTitle = styled(Typography)`
  color: ${props => props.theme.palette.text.primary} !important;
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 24px;
  line-height: 1.2;
  padding-bottom: 16px;
`;

const CardSubtitle = styled(Typography)`
  color: ${props => props.theme.palette.text.secondary} !important;
  font-size: 12px;
  line-height: 1.5;
  font-weight: 400;
`;

const MAX_ZOOM_SCALE = 4;

export const SizeCard = () => {
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [zoomScale, setZoomScale] = React.useState(MAX_ZOOM_SCALE);
  const [isManualControl, setIsManualControl] = React.useState(false);
  const controls = useAnimation();

  // Autoplay zoom animation
  useEffect(() => {
    const animate = async () => {
      // Create a slow breathing zoom effect
      await controls.start({
        scale: [1.5, 3.5, 1.5],
        transition: {
          duration: 60,
          ease: "easeInOut",
          repeat: Infinity,
          repeatType: "loop"
        }
      });
    };
    
    animate();
  }, [controls]);

  // Restart autoplay when manual control ends
  useEffect(() => {
    if (!isManualControl) {
      const animate = async () => {
        await controls.start({
          scale: [1.5, 3.5, 1.5],
          transition: {
            duration: 60,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "loop"
          }
        });
      };
      
      animate();
    }
  }, [isManualControl, controls]);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (cardRef.current) {
      setIsManualControl(true);
      const rect = cardRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      // Calculate normalized position (0 to 1)
      const normalizedX = x / rect.width;
      const normalizedY = y / rect.height;
      
      // Calculate zoom scale based on distance from center (3.0 to 1.0)
      const distanceFromCenter = Math.sqrt(
        Math.pow(normalizedX - 0.5, 2) + Math.pow(normalizedY - 0.5, 2)
      );
      const maxDistance = Math.sqrt(0.5 * 0.5 + 0.5 * 0.5); // Distance from center to corner
      const normalizedDistance = 1 - distanceFromCenter / maxDistance;
      const zoomScale = MAX_ZOOM_SCALE - (MAX_ZOOM_SCALE - 1) * normalizedDistance; // Scale from 3.0 (center) to 1.0 (edges)
    
      // Update state values for animation
      setZoomScale(zoomScale);
    }
  };

  const handleMouseLeave = () => {
    setIsManualControl(false);
    setZoomScale(MAX_ZOOM_SCALE);
  };

  return (
    <FeatureCard 
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        data-motion
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url(/ny-zoom.png)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          opacity: 0.8,
          zIndex: 1,
          borderRadius: '16px',
        }}
        animate={isManualControl ? {
          scale: zoomScale,
          backgroundPosition: `50% 50%`,
        } : controls}
        transition={{
          scale: {
            type: "spring",
            stiffness: 300,
            damping: 30
          },
          backgroundPosition: {
            type: "tween",
            ease: "easeOut",
            duration: 0.1
          }
        }}
      />
      
      <CardContent>
        <CardTitle variant="h4">
          Any Size
        </CardTitle>
        <CardSubtitle variant="body1">
          Pixel perfect maps of any size from A5 all the way up to A0
        </CardSubtitle>
      </CardContent>
    </FeatureCard>
  )
};
